<template>
  <b-container fluid class="comparative-analysis-table-print">
    <div class="comparative-analysis-table-print__title">
      <strong>
        {{ title }}
      </strong>
    </div>
    <s-loader
      v-if="loading"
      class="mt-2"
      size="lg"
    />
    <div v-else>
      <summary-transformer-info
        class="my-4"
        :loading.sync="loadingSummary"
      />
      <div
        v-for="(rule, index) in sortedRules"
        :key="index"
      >
        <strong class="comparative-analysis-table-print__rule-title">
          {{ $t(`enums.engineering.chromatography.rules.${rule}`) }}
        </strong>
        <s-table
          class="comparative-analysis-table-print__table"
          small
          :items="infos"
          :fields="getRuleFields(rule)"
        >
          <template #cell(classification-doernenburg)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.item.doernenburg.classification }}
            </div>
          </template>
          <template #cell(classification-rogers)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.item.rogers.classification }}
            </div>
          </template>
          <template #cell(classification-triangle-1)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.item['triangle-1'].classification }}
            </div>
          </template>
          <template #cell(classification-triangle-4)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.item['triangle-4'].classification }}
            </div>
          </template>
          <template #cell(classification-triangle-5)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.item['triangle-5'].classification }}
            </div>
          </template>
           <template #cell(classification-gases-ratio-2012)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.item['gases-ratio-2012'].classification }}
            </div>
          </template>
          <template #cell(classification-gases-ratio-2015)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.item['gases-ratio-2015'].classification }}
            </div>
          </template>
           <template #cell(doernenburg)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.value.description }}
            </div>
          </template>
          <template #cell(gases-ratio-2012)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.value.description }}
            </div>
          </template>
          <template #cell(gases-ratio-2015)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.value.description }}
            </div>
          </template>
          <template #cell(rogers)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.value.description }}
            </div>
          </template>
          <template #cell(triangle-1)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.value.description }}
            </div>
          </template>
          <template #cell(triangle-4)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.value.description }}
            </div>
          </template>
          <template #cell(triangle-5)="data">
            <div class="comparative-analysis-table-print__display-print">
              {{ data.value.description }}
            </div>
          </template>
        </s-table>
      </div>
    </div>
  </b-container>
</template>

<script>
import print from '@/services/print';
import LoadingMixin from '@/services/loadingMixin';
import SummaryTransformerInfo from '@/domain/components/installation/supervision/SummaryTransformerInfo';
import Service from '@/domain/services/installation/engineering/chromatography/comparativeAnalysis/index';
import { Rules, ClassificationType } from 'sigma-mappings';
import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';

export default {
  name: 'PrintView',
  components: {
    SummaryTransformerInfo,
  },
  mixins: [LoadingMixin],
  data: () => ({
    rules: Rules,
    infos: [],
    loadingSummary: false,
  }),
  computed: {
    sortedRules() {
      return Object.values(this.rules).sort().reverse();
    },
    title() {
      return this.$t('engineering.chromatography.methods-and-diagnostics.comparative-analysis.print-table-title');
    },
  },
  created() {
    this.getInfos();
  },
  methods: {
    getTableColumns(rule) {
      let ruleMethods = [];
      this.infos.forEach((sample) => {
        const ruleSamples = sample.norms.filter(norm => norm.norm === rule);
        if (ruleSamples.length) {
          ruleSamples.forEach((ruleSample) => {
            ruleMethods = ruleMethods.concat(ruleSample.methods);
          });
        }
      });
      ruleMethods = uniqBy(ruleMethods, 'method');
      return ruleMethods.map(method => ([
        {
          key: `classification-${method.method}`,
          label: this.$t('general.classification'),
        },
        {
          key: method.method,
          label: this.$t(`engineering.chromatography.methods-and-diagnostics.comparative-analysis.print-table-methods.${rule}.${method.method}`),
        },
      ]));
    },
    getRuleFields(rule) {
      const tableColumns = [...this.getTableColumns(rule)];
      const columnsFields = [
        {
          key: 'sampleNumber',
          thClass: 'comparative-analysis-table-print__sample-col',
          label: this.$t('fields.engineering.chromatography.sample'),
        },
      ];
      tableColumns.forEach(column => column.forEach(field => columnsFields.push(field)));
      return columnsFields;
    },
    handleDiagnosisValue(method) {
      return method.diagnostics.map(diagnosis => diagnosis.description.replace(/\.([^/.]*)$/, '$1')).join('. ') || '-';
    },
    handleClassificationValue(method) {
      const classificationTypes = {
        [ClassificationType.ORDINARY]: this.$t('enums.commons.classification-type.ordinary'),
        [ClassificationType.URGENT]: this.$t('enums.commons.classification-type.urgent'),
        [ClassificationType.CRITICAL]: this.$t('enums.commons.classification-type.critical'),
        [ClassificationType.OBSERVATION]: this.$t('enums.commons.classification-type.observation'),
      };
      const classificationsList = method.diagnostics
        .map(diagnosis => classificationTypes[diagnosis.classification]);
      return uniq(classificationsList).map(diagnosis => (diagnosis || '')
        .replace(/\.([^/.]*)$/, '$1')).join(', ') || '-';
    },
    mapDataWithMethodsAndDiagnosis(data) {
      return data.map((info) => {
        const methods = {};
        info.norms.forEach((norm) => {
          norm.methods.forEach((method) => {
            methods[method.method] = {
              description: this.handleDiagnosisValue(method),
              classification: this.handleClassificationValue(method),
            };
          });
        });
        return {
          ...info,
          sampleNumber: info.sampleNumber,
          ...methods,
        };
      });
    },
    getInfos() {
      const { assetModuleId, sampleIds } = this.$route.query;
      this.startLoading();
      Service.getComparativeAnalysis(assetModuleId, {
        page: 1,
        pageSize: 500,
        filter: {
          samplesIds: sampleIds,
        },
      }).then(({ data }) => {
        this.infos = this.mapDataWithMethodsAndDiagnosis(data);
        this.callPrint();
      }).finally(() => {
        this.endLoading();
      });
    },
    callPrint() {
      if (!this.loading && !this.loadingSummary) {
        setTimeout(() => {
          print();
        }, 1000);
      }
    },
  },
  watch: {
    loadingSummary() {
      this.callPrint();
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .comparative-analysis-table-print
    padding: 10px 20px
    &__title
      font-size: 1.2rem
      padding: 10px
      text-align: center
    &__rule-title
      display: block
      background-color: var(--color-background-dark)
      margin: 30px 0 15px
      padding: 3px 15px
    ::v-deep
      .comparative-analysis-table-print
        &__sample-col
          width: 75px
      tr td div
        page-break-inside: avoid
      th, td
        border: 1px solid $color-gray-intermediary
      tr td:last-child, tr th:last-child
        text-align: center !important
  @media print
    .comparative-analysis-table-print
      font-family: "Trebuchet MS", Helvetica, sans-serif
      &__table
        padding: 1px
      &__display-print
        display: block
        float: none
  @page
    size: A4 landscape !important
</style>
